import type {
  ConveyedPersonAction,
  PersonInvolvementKind,
} from "../types/involvement.js";

/**
 * The most a live involvement of each kind conveys, before the role its holder
 * acts in narrows it: `personActionsFromInvolvementKindInRole` applies the
 * role, held at the organisation the involvement came through. So users in the
 * assessor and responsible user roles at the organisation holding the duty of
 * care do not hold the same thing. Keeping the narrowing out of here is what
 * lets an access view report what an involvement means without being handed a
 * request.
 *
 * The mapping lives in one place because it is the whole policy. Stored per row
 * instead, what each involvement conveys would be a private arrangement and
 * "who may read this person" would stop being answerable in aggregate.
 *
 * A total `Record` rather than a function with branches, so admitting a kind of
 * involvement without deciding what it conveys does not compile.
 */
export const personActionsConveyedByInvolvementKind: Record<
  PersonInvolvementKind,
  readonly ConveyedPersonAction[]
> = {
  // The strongest claim anybody has: the organisation accountable for the person.
  duty_of_care: ["read", "read_assessments", "read_log", "update"],
  // The organisation looking after the person day to day.
  placement: ["read", "read_assessments", "update"],
  // Provenance made reachable: it keeps a person readable by the organisation
  // that created their record, and conveys `allocate_responsible_user`.
  // Creating a person record is not a part in the person's care, so it reads
  // no assessment.
  created_by: ["read", "allocate_responsible_user"],
  // Decides who is involved, so answerable for who has reach, which is what
  // the involvements started and ended show.
  responsible_user: [
    "read",
    "read_assessments",
    "read_log",
    "update",
    "assign",
    "involve_guest",
  ],
  // A contributing assessor's, and asking a guest, which a contributing
  // assessor may not: whose assessment stands decides whose view is sought.
  designated_assessor: ["read", "read_assessments", "assess", "involve_guest"],
  assessor: ["read", "read_assessments", "assess"],
  // Narrowed further by the assessor on the row: an approver reads that
  // assessor's work, not everything about the person, and approves the
  // definitive assessment, which is the only kind that needs approving.
  approver: ["read", "read_assessments", "approve", "involve_guest"],
  viewer: ["read", "read_assessments"],
  // Narrowed further by the assessments shared through it: a guest reads those
  // and no other, so a later assessment needs a share of its own
  // (`maySharedAssessmentReaderRead`).
  shared_assessment_reader: ["read", "read_assessments"],
};
